/**
 * UTM capture: URL → __utmzz cookie → sessionStorage → organic Google heuristic.
 * Use resolveUtmParams() when building API payloads to avoid Redux timing gaps.
 */
import Cookies from 'js-cookie';

export const UTM_FIELD_KEYS = [
	'utm_source',
	'utm_medium',
	'utm_campaign',
	'utm_content',
	'utm_term',
	'gclid',
	'dclid',
];

const SESSION_KEY = 'bk_utm_params';

export function hasAnyUtm(params = {}) {
	return UTM_FIELD_KEYS.some((key) => Boolean(params[key]));
}

function pickUtmFields(source = {}) {
	const out = {};
	for (const key of UTM_FIELD_KEYS) {
		const value = source[key];
		if (value != null && String(value).trim() !== '') {
			out[key] = String(value).trim();
		}
	}
	return out;
}

export function persistUtmParams(params) {
	const picked = pickUtmFields(params);
	if (!hasAnyUtm(picked)) return;
	try {
		const existing = getStoredUtmParams();
		sessionStorage.setItem(
			SESSION_KEY,
			JSON.stringify({ ...existing, ...picked })
		);
	} catch {
		// sessionStorage unavailable (private mode, etc.)
	}
}

export function getStoredUtmParams() {
	try {
		const raw = sessionStorage.getItem(SESSION_KEY);
		if (!raw) return {};
		const parsed = JSON.parse(raw);
		return pickUtmFields(parsed);
	} catch {
		return {};
	}
}

function parseUtmzzCookie() {
	const out = {
		utm_source: '',
		utm_medium: '',
		utm_campaign: '',
		utm_content: '',
		utm_term: '',
	};
	const utmzz = Cookies.get('__utmzz');
	if (!utmzz) return out;

	for (const part of utmzz.split('|')) {
		const eq = part.indexOf('=');
		if (eq === -1) continue;
		const key = part.slice(0, eq);
		const value = part.slice(eq + 1) || '';
		if (key === 'utmcsr') out.utm_source = value;
		else if (key === 'utmccn') out.utm_campaign = value;
		else if (key === 'utmcmd') out.utm_medium = value;
		else if (key === 'utmctr') out.utm_term = value;
		else if (key === 'utmcct') out.utm_content = value;
	}
	return out;
}

function applyOrganicGoogleFallback(params) {
	if (hasAnyUtm(params)) return params;

	const referrer = document.referrer || '';
	const isOrganicGoogle =
		referrer &&
		/google\./i.test(referrer) &&
		!referrer.includes('googleads') &&
		!referrer.includes('adwords');

	if (isOrganicGoogle) {
		return { ...params, utm_source: 'google', utm_medium: 'organic' };
	}
	return params;
}

/**
 * Parses UTM parameters from URL, __utmzz cookie, and sessionStorage.
 * URL params take precedence over cookie/session values.
 */
export function parseUtmParams(searchString = window.location.search) {
	const urlParams = new URLSearchParams(searchString);
	const fromCookie = parseUtmzzCookie();
	const fromSession = getStoredUtmParams();

	const merged = {
		utm_source:
			urlParams.get('utm_source') ||
			fromCookie.utm_source ||
			fromSession.utm_source ||
			'',
		utm_medium:
			urlParams.get('utm_medium') ||
			fromCookie.utm_medium ||
			fromSession.utm_medium ||
			'',
		utm_campaign:
			urlParams.get('utm_campaign') ||
			fromCookie.utm_campaign ||
			fromSession.utm_campaign ||
			'',
		utm_content:
			urlParams.get('utm_content') ||
			fromCookie.utm_content ||
			fromSession.utm_content ||
			'',
		utm_term:
			urlParams.get('utm_term') ||
			fromCookie.utm_term ||
			fromSession.utm_term ||
			'',
		gclid: urlParams.get('gclid') || fromSession.gclid || '',
		dclid: urlParams.get('dclid') || fromSession.dclid || '',
	};

	const withOrganic = applyOrganicGoogleFallback(merged);
	if (hasAnyUtm(withOrganic)) {
		persistUtmParams(withOrganic);
	}
	return withOrganic;
}

/**
 * Resolves the best available UTM set from URL/cookie/session, Redux, and component state.
 */
export function resolveUtmParams({
	company_store = {},
	searchString = window.location.search,
	componentState = {},
} = {}) {
	const fromUrl = parseUtmParams(searchString);
	const fromStore = pickUtmFields(company_store);
	const fromState = pickUtmFields(componentState);
	const fromSession = getStoredUtmParams();

	const resolved = {};
	for (const key of UTM_FIELD_KEYS) {
		resolved[key] =
			fromUrl[key] ||
			fromStore[key] ||
			fromState[key] ||
			fromSession[key] ||
			'';
	}

	if (hasAnyUtm(resolved)) {
		persistUtmParams(resolved);
	}
	return resolved;
}

/**
 * Writes non-empty UTM fields onto target (first non-empty source wins per field).
 */
export function mergeUtmIntoPayload(target, ...sources) {
	if (!target) return target;
	for (const key of UTM_FIELD_KEYS) {
		for (const source of sources) {
			const value = source?.[key];
			if (value != null && String(value).trim() !== '') {
				target[key] = String(value).trim();
				break;
			}
		}
		if (target[key] == null) {
			target[key] = target[key] || '';
		}
	}
	return target;
}

/** Dispatch resolved UTMs into Redux company_store. */
export function dispatchUtmParams(dispatch, ACTIONS, params) {
	if (!hasAnyUtm(params)) return;

	const add = (id, data) => {
		if (data == null || data === '') return;
		dispatch(
			ACTIONS.addCompanyInfo({
				id,
				data: String(data),
				stateObject: 'company_store',
			})
		);
	};

	for (const key of UTM_FIELD_KEYS) {
		add(key, params[key]);
	}
	if (params.utm_medium === 'referral' && params.utm_campaign) {
		add('discountCode', params.utm_campaign);
	}
}

/** Merge UTMs into a payload copied from company_store (ASP / callback requests). */
export function prepareAspPayload(companyStore, extra = {}) {
	const data = { ...companyStore, ...extra };
	const utm = resolveUtmParams({
		company_store: companyStore,
		componentState: extra,
	});
	mergeUtmIntoPayload(data, utm, extra, companyStore);
	return data;
}
